'use client';

import { Search, Check } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useGroupBuyStore } from '@/stores/useGroupBuyStore';
import { cn } from '@/lib/utils';

export default function GroupBuySearchBar() {
  // Get values and actions from Zustand store
  const { searchTerm, setSearchTerm } = useGroupBuyStore();

  // Local state for the search input
  const [query, setQuery] = useState(searchTerm);
  const [searchCount, setSearchCount] = useState(0);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track search count in session storage
  useEffect(() => {
    const storedCount = sessionStorage.getItem('searchCount');
    if (storedCount) {
      setSearchCount(parseInt(storedCount, 10));
    }
  }, []);

  // Update local state when store changes
  useEffect(() => {
    setQuery(searchTerm);
  }, [searchTerm]);

  const updateSearchCount = () => {
    const newCount = searchCount + 1;
    setSearchCount(newCount);
    sessionStorage.setItem('searchCount', newCount.toString());
  };

  // --- Event Handlers ---
  const handleInputChange = (value: string) => {
    // Update query state immediately for responsive input field
    setQuery(value);

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      if (value.trim() && value.trim().length >= 2) {
        setSearchTerm(value.trim());
        updateSearchCount();
      } else {
        setSearchTerm('');
      }
    }, 500);
  };

  // --- Return statement ---
  return (
    <div className="relative flex-1">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <input
        type="text"
        value={query}
        onChange={(e) => handleInputChange(e.target.value)}
        placeholder="Search group buys by title, product, or category..."
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pl-9',
          'text-sm ring-offset-background file:border-0 file:bg-transparent',
          'file:text-sm file:font-medium placeholder:text-muted-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        )}
        maxLength={50}
      />
    </div>
  );
}
